/**
 * Per-message text direction.
 *
 * The document direction follows the interface language, but a single conversation mixes
 * languages: an employee working in Arabic pastes an English error message, or asks in English
 * about an Arabic circular. So each message gets its own direction from its own content, and the
 * document direction is only the default.
 *
 * `dir="auto"` on the element would do most of this natively — but it resolves from the first
 * strong directional character, so an Arabic answer opening with a Latin product name renders
 * left-to-right and looks broken. Deciding from the whole string is more reliable.
 */

import { detectLanguage } from "./detect";

export type Direction = "ltr" | "rtl";

/** Direction for one piece of content. */
export function directionOfText(text: string): Direction {
  return detectLanguage(text) === "ar" ? "rtl" : "ltr";
}

/**
 * Whether a message's direction differs from the surrounding interface.
 *
 * A message that opposes the page needs its own alignment and an isolation boundary; one that
 * agrees needs neither, and adding them anyway produces subtly wrong spacing.
 */
export function opposesDirection(text: string, pageDirection: Direction): boolean {
  return directionOfText(text) !== pageDirection;
}
